import { digger } from '@cbre/digger';
import { concatValuesBy, formatNumber } from '@miq/utils';
import { ReportRecord } from '../../../../../models/report.model';
import {
  DisplayFnArgumentsInfo,
  DisplayFunctionOptions,
  TemplateFieldsDisplayFn,
} from '../../../../../models/template.model';
import { Dash, displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { getUOMValue } from '../us.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  310: {
    // 310 Property Name
    displayFn: displayFnWrapper,
  },
  311: {
    // 311 Property Address
    displayFn: (record: ReportRecord, options, arg1, arg2, arg3, arg4) => {
      const values = [arg2, arg3, arg4].map(arg => mapAndFormatDynamicValue(record, arg));
      const topVal = mapAndFormatDynamicValue(record, arg1);
      const bottomVal = concatValuesBy(', ', ...values).replace(/,(?=[^,]*$)/, ''); // Removing last comma before PIN
      return concatValuesBy(' \n', topVal, bottomVal);
    },
  },
  312: {
    // 312 NRA
    displayFn: (record: ReportRecord, _, arg1) => {
      arg1.uomId = null;
      arg1.uomArgument = null;
      const nra = mapAndFormatDynamicValue(record, arg1);
      return nra;
    },
  },
  313: {
    // 313 Market Submarket
    displayFn: (record: ReportRecord, settings, market, submarket) => {
      const usage = getUsage(record, settings);
      if (usage.length && usage.length === 1) {
        const marketVal = mapAndFormatDynamicValue(
          { ...record, data: { ...record.data, usages: usage } },
          market
        ) as string[];
        const subMarketVal = mapAndFormatDynamicValue(
          { ...record, data: { ...record.data, usages: usage } },
          submarket
        ) as string[];
        return concatValuesBy(' \n', marketVal, subMarketVal);
      }
      return Dash + ' \n' + Dash;
    },
  },
  314: {
    // 314 Property Type Status
    displayFn: (record: ReportRecord, settings, arg1, arg2) => {
      const usage = getUsage(record, settings);
      if (usage.length && usage.length === 1) {
        const propertyType = mapAndFormatDynamicValue(
          { ...record, data: { ...record.data, usages: usage } },
          arg2
        ) as string[];
        const status = mapAndFormatDynamicValue(
          { ...record, data: { ...record.data, usages: usage } },
          arg1
        ) as string[];
        return concatValuesBy(' \n', propertyType, status);
      }
      return Dash;
    },
  },
  315: {
    //315 Class
    displayFn: (record: ReportRecord, settings, arg1) => {
      const usage = getUsage(record, settings);
      if (usage.length && usage.length === 1) {
        return mapAndFormatDynamicValue({ ...record, data: { ...record.data, usages: usage } }, arg1) as string[];
      }
      return Dash;
    },
  },
  316: {
    // 316 Year Built Reno
    displayFn: (record: ReportRecord, _, yearBuilt, reno) => {
      const yearBuiltVal = mapAndFormatDynamicValue(record, yearBuilt) as string[];
      const renoVal = mapAndFormatDynamicValue(record, reno) as string[];
      return concatValuesBy(' \n', yearBuiltVal, renoVal);
    },
  },
  317: {
    // 317 Total Avail Sf Avail %
    displayFn: (record: ReportRecord, settings, arg1, arg2) => {
      return getTotalSfAndPercentage(record, settings, arg1, arg2);
    },
  },
  318: {
    // 318 Total Vacant Sf Vacant %"
    displayFn: (record: ReportRecord, settings, arg1, arg2) => {
      return getTotalSfAndPercentage(record, settings, arg1, arg2);
    },
  },
  329: {
    // 329 - Total Size
    displayFn: (record: ReportRecord[], _, arg1) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const size = digger(rec.data, arg1.field);
        const uomValue = digger(rec.data, arg1.uomArgument.field);
        uom = getUOMValue(uom, uomValue);
        if (size) {
          totalSize += parseInt(size);
        }
      });
      return formatNumber(totalSize) + ' ' + 'SF';
    },
  },
};

function getUsageTypes(office: boolean, retail: boolean, industrial: boolean) {
  let usageType = [];
  if (office) {
    usageType.push('Office');
  }
  if (retail) {
    usageType.push('Retail');
  }
  if (industrial) {
    usageType.push('Industrial');
  }
  return usageType;
}

function getUsage(record: ReportRecord, settings: DisplayFunctionOptions) {
  const { office, retail, industrial } = settings.reportOptions;
  let usage = record.data.usages;
  const usageTypes = getUsageTypes(office, retail, industrial);
  if (usageTypes.length) {
    usage = record.data.usages.filter(usage => usageTypes.includes(usage.ref_property_usage_type_desc));
  }
  if (usage.length && usage.length !== 1) {
    //check for primary
    const primaryUsage = usage.filter(a => a.primary_usage_f === true);
    if (primaryUsage && primaryUsage.length === 1) {
      usage = primaryUsage;
    } else {
      if (primaryUsage && primaryUsage.length > 1) {
        usage = primaryUsage;
      }
      //check largest nra and latest
      usage = usage.reduce((item, curr) => {
        if (item.net_rentable_area === curr.net_rentable_area) {
          return new Date(item.entity.edp_created_date || item.entity.edp_last_update_date) <
            new Date(curr.entity.edp_created_date || curr.entity.edp_last_update_date)
            ? [curr]
            : [item];
        } else {
          return item.net_rentable_area < curr.net_rentable_area ? [curr] : [item];
        }
      });
    }
  }
  return usage;
}

function getTotalSfAndPercentage(
  record: ReportRecord,
  settings: DisplayFunctionOptions,
  arg1: DisplayFnArgumentsInfo,
  arg2: DisplayFnArgumentsInfo
) {
  const usage = getUsage(record, settings);
  if (usage.length && usage.length === 1) {
    const totalAvailSF = mapAndFormatDynamicValue({ ...record, data: { ...record.data, usages: usage } }, arg2);
    const totalAvail = mapAndFormatDynamicValue(
      { ...record, data: { ...record.data, usages: usage } },
      {
        dataType: 'id',
        entity: arg2.entity,
        field: arg2.field,
        id: arg2.id,
      }
    );
    if (totalAvail[0] != null && !Number.isNaN(Number(totalAvail[0]))) {
      const nra = mapAndFormatDynamicValue(
        { ...record, data: { ...record.data, usages: usage } },
        {
          dataType: 'id',
          entity: arg1.entity,
          field: arg1.field,
          id: arg1.id,
        }
      );
      const percentage =
        nra[0] != null && !Number.isNaN(nra[0])
          ? Math.round((Number(totalAvail[0]) / Number(nra[0])) * 100) + '%'
          : Dash;
      return concatValuesBy('\n ', totalAvailSF, percentage);
    }
  }
  return Dash + '\n ' + Dash;
}
